import { useState, useEffect, useCallback } from 'react';
import { useRestaurant } from '../../context/RestaurantContext';

const useFetchTalents = () => {
    const [talents, setTalents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { restaurantId, loading: restaurantLoading } = useRestaurant();

    const fetchTalents = useCallback(async () => {
        // Don't fetch if restaurant context is still loading or no restaurant ID
        if (restaurantLoading || !restaurantId) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const url = `${process.env.REACT_APP_BACKEND_STAGING_URL}/api/talent-pool?restaurantId=${restaurantId}`;
            
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include',
            });


            if (!response.ok) {
                const errorText = await response.text();
                console.error('🔍 [useFetchTalents] Response not ok:', response.status, errorText);
                throw new Error('Failed to fetch talents');
            }

            const data = await response.json();
            
            // Ensure we always set an array, handle both direct array and wrapped responses
            const talentsArray = Array.isArray(data) ? data : (data.talents || data.data || []);
            setTalents(talentsArray);
        } catch (error) {
            console.error('🔍 [useFetchTalents] Error fetching talents:', error);
            setError(error.message);
            setTalents([]); // Ensure talents remains an empty array on error
        } finally {
            setLoading(false);
        }
    }, [restaurantId, restaurantLoading]);

    const triggerFetchTalents = useCallback(() => {
        fetchTalents();
    }, [fetchTalents]);

    useEffect(() => {
        fetchTalents();
    }, [fetchTalents, restaurantId]);

    // Listen for restaurant context updated events
    useEffect(() => {
        const handleRestaurantContextUpdated = () => {
            fetchTalents();
        };

        window.addEventListener('restaurantContextUpdated', handleRestaurantContextUpdated);
        
        return () => {
            window.removeEventListener('restaurantContextUpdated', handleRestaurantContextUpdated);
        };
    }, [fetchTalents]);

    return { talents, loading, error, setTalents, triggerFetchTalents };
};

export default useFetchTalents;
